import { useState } from 'react';
import { Tooltip } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import { LoadingOutlined } from '@ant-design/icons';
import { saveAs } from 'file-saver';
import { Download } from 'assets/svg';
import { sanitizeSheetName } from 'utils/excelHelpers';

/** A single sheet: tab name + row-major data (first row is typically headers) */
export interface ExcelSheet {
    name: string;
    rows: (string | number)[][];
}

interface DownloadMultiSheetExcelProps {
    sheets: ExcelSheet[];
    fileName: string;
    disabled?: boolean;
    tooltipText?: string;
    tooltipPlacement?: TooltipPlacement;
    iconSize?: number;
}

function DownloadMultiSheetExcel({
    sheets,
    fileName,
    disabled = false,
    tooltipText = 'Export Data',
    tooltipPlacement = 'left',
    iconSize = 13,
}: DownloadMultiSheetExcelProps) {
    const [loading, setLoading] = useState(false);

    const handleExport = async () => {
        if (loading || disabled || !sheets.length) return;
        setLoading(true);
        try {
            const XLSX = await import('xlsx');
            const wb = XLSX.utils.book_new();

            sheets.forEach((sheet, index) => {
                const ws = XLSX.utils.aoa_to_sheet(sheet.rows);
                const sheetName = sanitizeSheetName(sheet.name, `Sheet_${index + 1}`);
                XLSX.utils.book_append_sheet(wb, ws, sheetName);
            });

            const wBook = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wBook], { type: 'application/octet-stream' });
            saveAs(blob, `${fileName}.xlsx`);
        } catch (err) {
            console.error('Multi-sheet export failed', err);
        } finally {
            setLoading(false);
        }
    };

    const isDisabled = disabled || !sheets.length;

    return (
        <Tooltip title={tooltipText} placement={tooltipPlacement}>
            <div
                onClick={handleExport}
                className={`h-fit ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
            >
                {loading ? <LoadingOutlined style={{ fontSize: iconSize }} spin /> : <Download />}
            </div>
        </Tooltip>
    );
}

export default DownloadMultiSheetExcel;
